import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Landing from "./pages/Landing";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import PlanCuentas from "./pages/PlanCuentas";
import CentrosCosto from "./pages/CentrosCosto";
import LibroDiario from "./pages/LibroDiario";
import LibroMayor from "./pages/LibroMayor";
import BalanceGeneral from "./pages/BalanceGeneral";
import Balance8Columnas from "./pages/Balance8Columnas";
import EstadoResultados from "./pages/EstadoResultados";
import { Clientes, Proveedores } from "./pages/Contactos";
import { LibroVentas, LibroCompras } from "./pages/LibrosIVA";
import BoletasHonorarios from "./pages/BoletasHonorarios";
import FlujoCaja from "./pages/FlujoCaja";
import CuentasBancarias from "./pages/CuentasBancarias";
import Cartola from "./pages/Cartola";
import Conciliacion from "./pages/Conciliacion";
import PagosProveedores from "./pages/PagosProveedores";
import CuentaCorriente from "./pages/CuentaCorriente";
import CajaChica from "./pages/CajaChica";
import Empresas from "./pages/Empresas";
import Usuarios from "./pages/Usuarios";
import Remuneraciones from "./pages/remuneraciones/Remuneraciones";
import PortalColaborador from "./pages/remuneraciones/PortalColaborador";
import SubscriptionBlocked from "./components/SubscriptionBlocked";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#59645E]">
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const blocked =
    user.organizacion_expirada ||
    (user.organizacion_id && user.organizacion_activa === false);
  if (blocked && user.role !== "superadmin") {
    return <SubscriptionBlocked />;
  }
  return children;
}

function AuthLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center text-[#59645E]">
      Cargando…
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <AuthLoading />;
  if (user) return <Navigate to="/app/dashboard" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/olvide-contraseña" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
          <Route path="/restablecer-contraseña" element={<PublicOnly><ResetPassword /></PublicOnly>} />
          <Route path="/portal-colaborador" element={<PortalColaborador />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="plan-cuentas" element={<PlanCuentas />} />
            <Route path="centros-costo" element={<CentrosCosto />} />
            <Route path="libro-diario" element={<LibroDiario />} />
            <Route path="libro-mayor" element={<LibroMayor />} />
            <Route path="balance-general" element={<BalanceGeneral />} />
            <Route path="balance-8-columnas" element={<Balance8Columnas />} />
            <Route path="estado-resultados" element={<EstadoResultados />} />
            <Route path="clientes" element={<Clientes />} />
            <Route path="proveedores" element={<Proveedores />} />
            <Route path="libro-ventas" element={<LibroVentas />} />
            <Route path="libro-compras" element={<LibroCompras />} />
            <Route path="boletas-honorarios" element={<BoletasHonorarios />} />
            <Route path="flujo-caja" element={<FlujoCaja />} />
            <Route path="cuentas-bancarias" element={<CuentasBancarias />} />
            <Route path="cartola" element={<Cartola />} />
            <Route path="conciliacion" element={<Conciliacion />} />
            <Route path="cuenta-corriente" element={<CuentaCorriente />} />
            <Route path="pagos-proveedores" element={<PagosProveedores />} />
            <Route path="caja-chica" element={<CajaChica />} />
            <Route path="remuneraciones/*" element={<Remuneraciones />} />
            <Route path="empresas" element={<Empresas />} />
            <Route path="usuarios" element={<Usuarios />} />
          </Route>
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
